import { useEffect, useState, useCallback } from "react";
import { Loader2, Users, Plus, Copy, Trash2, KeyRound, LogOut, ShieldCheck, Eye, UserMinus, Power, History, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const timeAgo = (iso) => {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24); return `${d}d ago`;
};

export default function PortalTeamAccess() {
  const [ctx, setCtx] = useState(null);
  const [codes, setCodes] = useState([]);
  const [memberCount, setMemberCount] = useState(0);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [redeem, setRedeem] = useState("");
  const [busy, setBusy] = useState(false);
  const [newCode, setNewCode] = useState({ label: "", permission: "full" });
  const [showActivity, setShowActivity] = useState(() => {
    try { return localStorage.getItem("elevare_show_activity") === "1"; } catch { return false; }
  });

  const loadCodes = useCallback(async () => {
    try { const { data } = await api.get("/portal/team/codes"); setCodes(data.codes || []); setMemberCount(data.member_count || 0); }
    catch { /* member/403 — ignore */ }
    try { const { data } = await api.get("/portal/team/activity"); setActivity(data.activity || []); }
    catch { /* ignore */ }
  }, []);

  const load = useCallback(async () => {
    try {
      const { data: c } = await api.get("/portal/team/context");
      setCtx(c);
      if (!c.is_member) await loadCodes();
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [loadCodes]);
  useEffect(() => { load(); }, [load]);

  const doRedeem = async () => {
    if (!redeem.trim()) { toast.error("Enter your access code."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/portal/team/redeem", { code: redeem.trim() });
      toast.success(`You now have ${data.permission === "view" ? "view-only" : "full"} access to ${data.owner_business}. Reloading…`);
      setTimeout(() => window.location.reload(), 1200);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not redeem that code."); setBusy(false); }
  };

  const leave = async () => {
    if (!window.confirm("Leave this team and return to your own account?")) return;
    try { await api.post("/portal/team/leave"); toast.success("Left the team. Reloading…"); setTimeout(() => window.location.reload(), 1000); }
    catch { toast.error("Could not leave the team."); }
  };

  const createCode = async () => {
    setBusy(true);
    try { await api.post("/portal/team/codes", newCode); setNewCode({ label: "", permission: "full" }); await loadCodes(); toast.success("Access code created."); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not create the code."); }
    finally { setBusy(false); }
  };

  const toggleCode = async (c) => { try { await api.post(`/portal/team/codes/${c.id}/toggle`, { active: !c.active }); await loadCodes(); } catch { toast.error("Could not update the code."); } };
  const deleteCode = async (id) => { if (!window.confirm("Delete this code and revoke everyone who used it?")) return; try { await api.delete(`/portal/team/codes/${id}`); await loadCodes(); toast.success("Code deleted."); } catch { toast.error("Could not delete."); } };
  const revokeMember = async (mid) => { try { await api.post(`/portal/team/members/${mid}/revoke`); await loadCodes(); toast.success("Access revoked."); } catch { toast.error("Could not revoke."); } };
  const copy = (code) => { try { navigator.clipboard.writeText(code); toast.success("Code copied."); } catch { /* ignore */ } };

  if (loading) return null;

  // Delegated member view — just show context + leave.
  if (ctx?.is_member) {
    return (
      <div className="mb-6 rounded-2xl border border-[#c9a961]/50 bg-[#c9a961]/8 p-5" data-testid="team-member-banner">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-[#1e3d3a]">
            <Users className="h-4 w-4 text-[#c9a961]" />
            <span className="text-sm">You're accessing <b>{ctx.owner_business}</b>'s account · {ctx.permission === "view" ? "view-only" : "full access"}</span>
          </div>
          <button data-testid="team-leave" onClick={leave} className="inline-flex items-center gap-1.5 rounded-full border border-[#1e3d3a]/15 px-3 py-1.5 text-xs text-[#1e3d3a]/70 hover:bg-white"><LogOut className="h-3.5 w-3.5" /> Leave</button>
        </div>
      </div>
    );
  }

  const inp = "w-full rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-2 text-sm text-[#1e3d3a] focus:border-[#c9a961] focus:outline-none";

  return (
    <div className="mb-6 rounded-2xl border border-[#1e3d3a]/12 bg-white p-5 lg:p-6" data-testid="team-access">
      <div className="flex items-center gap-2 text-[#1e3d3a] mb-1"><Users className="h-4 w-4 text-[#c9a961]" /><h3 className="font-head text-lg">Team access</h3></div>
      <p className="text-xs text-[#1e3d3a]/55 mb-4">Give teammates access to your account with a code — they sign up with their own login and enter the code once. No identity check needed for them; access lasts until you turn it off.</p>

      {/* Redeem (for people invited to someone else's account) */}
      <div className="mb-5 rounded-xl border border-[#1e3d3a]/10 bg-[#f7f5ef] p-3">
        <div className="text-[11px] uppercase tracking-wide text-[#1e3d3a]/45 mb-1.5 flex items-center gap-1.5"><KeyRound className="h-3.5 w-3.5" /> Have an access code?</div>
        <div className="flex gap-2">
          <input data-testid="team-redeem-input" className={inp} placeholder="Enter access code (e.g. ABC-123-XYZ)" value={redeem} onChange={(e) => setRedeem(e.target.value)} />
          <button data-testid="team-redeem-btn" onClick={doRedeem} disabled={busy} className="rounded-full bg-[#c9a961] px-4 py-2 text-sm font-semibold text-[#1a2e2b] hover:bg-[#d8b972] disabled:opacity-50">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Join"}</button>
        </div>
      </div>

      {/* Create a code */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 mb-4">
        <input data-testid="team-code-label" className={`${inp} sm:col-span-2`} placeholder="Who's this for? (e.g. Bookkeeper)" value={newCode.label} onChange={(e) => setNewCode({ ...newCode, label: e.target.value })} />
        <select data-testid="team-code-permission" className={inp} value={newCode.permission} onChange={(e) => setNewCode({ ...newCode, permission: e.target.value })}>
          <option value="full">Full access</option>
          <option value="view">View-only</option>
        </select>
        <button data-testid="team-code-create" onClick={createCode} disabled={busy} className="inline-flex items-center justify-center gap-1.5 rounded-full bg-[#1e3d3a] px-4 py-2 text-sm font-semibold text-white hover:bg-[#2a534d] disabled:opacity-50">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Create code</button>
      </div>

      {codes.length > 0 && (
        <ul className="space-y-3" data-testid="team-codes">
          {codes.map((c) => (
            <li key={c.id} data-testid={`team-code-${c.id}`} className={`rounded-xl border p-3 ${c.active ? "border-[#1e3d3a]/12" : "border-[#1e3d3a]/8 bg-[#1e3d3a]/3 opacity-70"}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <button data-testid={`team-code-copy-${c.id}`} onClick={() => copy(c.code)} className="inline-flex items-center gap-1.5 font-mono text-sm font-semibold text-[#1e3d3a] hover:text-[#a3823f]" title="Copy code">{c.code} <Copy className="h-3.5 w-3.5" /></button>
                    <span className={`rounded-full px-2 py-0.5 text-[9px] uppercase tracking-wide ${c.permission === "view" ? "bg-[#3b6ea5]/12 text-[#3b6ea5]" : "bg-[#2f7d5b]/12 text-[#2f7d5b]"}`}>{c.permission === "view" ? <span className="inline-flex items-center gap-1"><Eye className="h-3 w-3" />view-only</span> : <span className="inline-flex items-center gap-1"><ShieldCheck className="h-3 w-3" />full</span>}</span>
                    {!c.active && <span className="rounded-full bg-[#1e3d3a]/8 px-2 py-0.5 text-[9px] uppercase tracking-wide text-[#1e3d3a]/50">off</span>}
                  </div>
                  {c.label && <div className="text-[11px] text-[#1e3d3a]/50 mt-0.5">{c.label}</div>}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button data-testid={`team-code-toggle-${c.id}`} onClick={() => toggleCode(c)} title={c.active ? "Turn off" : "Turn on"} className={`p-1.5 rounded-lg ${c.active ? "text-[#1e3d3a]/55 hover:text-[#b23a48]" : "text-[#2f7d5b] hover:bg-[#2f7d5b]/10"}`}><Power className="h-4 w-4" /></button>
                  <button data-testid={`team-code-delete-${c.id}`} onClick={() => deleteCode(c.id)} className="p-1.5 rounded-lg text-[#1e3d3a]/30 hover:text-[#b23a48]"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              {c.members.filter((m) => m.active).length > 0 && (
                <div className="mt-2 border-t border-[#1e3d3a]/8 pt-2 space-y-1">
                  {c.members.filter((m) => m.active).map((m) => (
                    <div key={m.id} data-testid={`team-member-${m.id}`} className="flex items-center justify-between text-[13px] text-[#1e3d3a]/80">
                      <span className="truncate">{m.member_name}</span>
                      <button data-testid={`team-member-revoke-${m.id}`} onClick={() => revokeMember(m.id)} className="inline-flex items-center gap-1 text-[11px] text-[#1e3d3a]/40 hover:text-[#b23a48]"><UserMinus className="h-3.5 w-3.5" /> revoke</button>
                    </div>
                  ))}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {activity.length > 0 && (
        <div className="mt-5 border-t border-[#1e3d3a]/8 pt-4" data-testid="team-activity">
          <button
            type="button"
            data-testid="team-activity-toggle"
            onClick={() => setShowActivity((v) => { const nv = !v; try { localStorage.setItem("elevare_show_activity", nv ? "1" : "0"); } catch { /* ignore */ } return nv; })}
            className="w-full flex items-center gap-2 text-[#1e3d3a] hover:text-[#a3823f] transition-colors duration-200"
          >
            <History className="h-4 w-4 text-[#c9a961]" />
            <span className="text-sm font-semibold">Recent activity</span>
            <span className="text-[11px] text-[#1e3d3a]/45">({activity.length})</span>
            {showActivity
              ? <ChevronUp className="h-4 w-4 ml-auto text-[#1e3d3a]/40" />
              : <ChevronDown className="h-4 w-4 ml-auto text-[#1e3d3a]/40" />}
          </button>
          {showActivity && (
            <>
              <p className="text-[11px] text-[#1e3d3a]/50 mt-2 mb-2">Who signed in and what they did on your account.</p>
              <ul className="space-y-1.5 max-h-56 overflow-y-auto">
                {activity.map((a) => (
                  <li key={a.id} className="flex items-center justify-between gap-3 text-[13px]">
                    <span className="text-[#1e3d3a] truncate">
                      <span className={`inline-block h-1.5 w-1.5 rounded-full mr-2 align-middle ${a.type === "login" ? "bg-[#3b6ea5]" : "bg-[#c9a961]"}`} />
                      <b>{a.actor_name || a.actor_email}</b> · {a.label}
                    </span>
                    <span className="text-[11px] text-[#1e3d3a]/45 shrink-0">{timeAgo(a.at)}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}
